import i18n from '../../../i18n';
import { getDepartmentName } from '../../../hooks/useDepartments';
import { notAvailable } from '../../../utils/formatters';
import {
  getTemperatureFromInsight,
  getTemperatureColorFromInsight,
} from '../../../components/TemperatureChip/temperature';

export const formatTimeAgo = (timestamp) => {
  if (!timestamp) return notAvailable();
  const diffMs = Date.now() - new Date(timestamp).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 60) return `${diffMins} min`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h`;
  return `${Math.floor(diffHours / 24)}d`;
};

export const getSentimentEmoji = (sentiment) => {
  const map = { 5: '😄', 4: '😊', 3: '😐', 2: '🙁', 1: '😞' };
  return map[sentiment] || '';
};

export const getSentimentText = (sentiment) => {
  if (!sentiment) return '';
  const map = {
    5: i18n.t('leads.sentimentVeryPositive'),
    4: i18n.t('leads.sentimentPositive'),
    3: i18n.t('leads.sentimentNeutral'),
    2: i18n.t('leads.sentimentNegative'),
    1: i18n.t('leads.sentimentVeryNegative'),
  };
  return map[sentiment] || '';
};

export const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.split(' ');
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
};

export const transformConversation = (conv, departments = []) => ({
  id: conv.uuid,
  uuid: conv.uuid,
  status: conv.status,
  unreadCount: conv.unread_count,
  assignedTo: conv.assigned_to_id,
  assignedToName: conv.assigned_to_name || '',
  lastAssignedToName: conv.last_assigned_to_name || '',
  hasHumanInteraction: conv.has_human_interaction ?? false,
  waitingForHuman: conv.waiting_for_human ?? false,
  waitingForHumanSince: conv.waiting_for_human_since || null,
  lastMessageAt: conv.last_message_at,
  lastMessageText: conv.last_message_text || '',
  lastMessageType: conv.last_message_type || 'text',
  lastMessageDuration: conv.last_message_duration ?? null,
  name: conv.contact.display_name,
  phone: conv.contact?.phone_number || '',
  contactUuid: conv.contact?.uuid || '',
  timeAgo: formatTimeAgo(conv.created_at),
  hasInsight: !!conv.insight,
  temperatureValue: conv.insight?.temperature,
  temperature: getTemperatureFromInsight(conv.insight?.temperature),
  temperatureColor: getTemperatureColorFromInsight(conv.insight?.temperature),
  context: conv.insight?.summary || '',
  interest: conv.insight?.interested_in || '',
  exchange: conv.insight?.trade_in || '',
  trigger: conv.insight?.trigger || '',
  sentiment: getSentimentText(conv.insight?.sentiment),
  sentimentEmoji: getSentimentEmoji(conv.insight?.sentiment),
  department: getDepartmentName(departments, conv.latest_department),
  targetBusiness: conv.latest_target_business || '',
  campaignId: conv.campaign?.uuid || null,
  campaignTitle: conv.campaign?.headline || conv.campaign?.name || '',
  campaignExternalId: conv.campaign?.external_id || '',
  originId: conv.origin?.uuid || null,
  originName: conv.origin?.name || '',
  seller: conv.assigned_to?.name || conv.assigned_to?.email || notAvailable(),
  avatar: getInitials(conv.contact?.display_name),
  readOnly: !conv.authorization?.state_flags?.human_action_allowed,
  isExpired: conv.authorization?.state_flags?.is_expired ?? false,
  canAccept: conv.authorization?.capabilities?.can_accept ?? false,
  canRelease: conv.authorization?.capabilities?.can_release ?? false,
  isAssignedToMe: conv.is_assigned_to_me ?? false,
  canTransfer: conv.authorization?.capabilities?.can_transfer ?? false,
  canClose: conv.authorization?.capabilities?.can_close ?? false,
  canReopen: conv.authorization?.capabilities?.can_reopen ?? false,
  canViewMessages: conv.authorization?.capabilities?.can_view_messages ?? false,
  canSendMessages: conv.authorization?.capabilities?.can_send_messages ?? false,
  canPreview: conv.authorization?.capabilities?.can_preview ?? false,
  canViewHistory: conv.authorization?.capabilities?.can_view_history ?? false,
  canViewContact: conv.authorization?.capabilities?.can_view_contact ?? false,
  isPinned: conv.is_pinned ?? false,
  pinPosition: conv.pin_position ?? null,
  isFavorite: conv.is_favorite ?? false,
});
